import type {Vector2Like} from "@cm/math"
import {Vector2} from "@cm/math"
import type {MattressReferenceFrameType} from "#mattress/validation"
import type {MattressMeshGeometryParametersData} from "#mattress/nodes/component/mattress-component-types"

// Reference frame UV (cylindrical coordinates)
// V parallel to global axis specifed by type (e.g. height)
// U orthogonal to V, "wraps around" the mattress, normalized to range [-1,1] with
// 0 - front center
// 0.2 - start right front corner
// 0.3 - end right front corner
// 0.7 - start right back corner
// 0.8 - end right back corner
// 1.0 - back center (same as -1.0)
// left side set up symmetrically

// height reference frame uv space u coordinate -> to uv on unit square with corners at [-1,-1], [1,-1], [1,1], [-1,1]
// corner size for unit square 0.2 (see sc2 in mattress geometry definition)
// coresponding corner size in reference frame uv space is 0.05 (see comment above)

export const referenceFrameUParams = {
    bottomCenter: 0,
    bottomRight: 0.25,
    rightCenter: 0.5,
    topRight: 0.75,
    topCenter: {right: 1.0, left: -1.0},
    bottomLeft: -0.25,
    leftCenter: -0.5,
    topLeft: -0.75,
    cornerRadius: 0.05,
}
export const unitSquareUVParams = {
    bottomCenter: {x: 0, y: -1},
    bottomRight: {x: 1, y: -1},
    rightCenter: {x: 1, y: 0},
    topRight: {x: 1, y: 1},
    topCenter: {x: 0, y: 1},
    bottomLeft: {x: -1, y: -1},
    leftCenter: {x: -1, y: 0},
    topLeft: {x: -1, y: 1},
    cornerRadius: 0.4,
}

export const canonicalUVParams = {
    topRoundedEdgeInnerHalfSide: 0.8 ** 5,
    topRoundedEdgeOuterHalfSide: 0.8 ** 4,
    sidesRegionInnerHalfSide: 0.8 ** 4, // top
    sidesRegionOuterHalfSide: 0.8 ** 3, // bottom
    bottomRoundedEdgeInnerHalfSide: 0.8 ** 3,
    bottomRoundedEdgeOuterHalfSide: 0.8 ** 2,
}

//////////////////////////////////////////////////////////////////////////////////////////////////
// Geometry helpers

export function isPointInSquare(point: Vector2Like, halfSide: number): boolean {
    return point.x > -halfSide && point.x < halfSide && point.y > -halfSide && point.y < halfSide
}

export function clipPointToSquare(point: Vector2Like, halfSide: number): Vector2Like {
    if (point.x === 0) return {x: 0, y: Math.sign(point.y) * halfSide}
    if (point.y === 0) return {x: Math.sign(point.x) * halfSide, y: 0}

    const ratio = point.y / point.x
    if (Math.abs(point.x) >= Math.abs(point.y)) return {x: Math.sign(point.x) * halfSide, y: Math.sign(point.x) * halfSide * ratio}
    else return {x: (Math.sign(point.y) * halfSide) / ratio, y: Math.sign(point.y) * halfSide}
}

export function pointRadius(point: Vector2Like): number {
    return Math.max(Math.abs(point.x), Math.abs(point.y))
}

// Projects a 2D point radially onto the unit square boundary
export function mapToUnitSquareUV(uv: Vector2Like): Vector2Like {
    if (uv.x === 0) return {x: 0, y: Math.sign(uv.y)}
    else if (uv.y === 0) return {x: Math.sign(uv.x), y: 0}
    const ratio = uv.y / uv.x
    return Math.abs(uv.x) >= Math.abs(uv.y) ? {x: Math.sign(uv.x), y: Math.sign(uv.x) * ratio} : {x: Math.sign(uv.y) / ratio, y: Math.sign(uv.y)}
}

export function mapPointBetweenSquareBands(
    point: Vector2Like,
    fromOuterHalfSide: number,
    fromInnerHalfSide: number,
    toOuterHalfSide: number,
    toInnerHalfSide: number,
): Vector2Like {
    const unitSquareUV = mapToUnitSquareUV(point)
    const t = (pointRadius(point) - fromInnerHalfSide) / (fromOuterHalfSide - fromInnerHalfSide)
    return Vector2.fromVector2Like(unitSquareUV).mul(toInnerHalfSide + t * (toOuterHalfSide - toInnerHalfSide))
}

//////////////////////////////////////////////////////////////////////////////////////////////////
// Interval and interpolation utilities

export function isInInterval(value: number, start: number, end: number): boolean {
    return value >= start && value <= end
}

export function remapNumber(value: number, fromMin: number, fromMax: number, toMin: number, toMax: number): number {
    const t = (value - fromMin) / (fromMax - fromMin)
    return toMin + t * (toMax - toMin)
}

export function remapVector2(value: number, fromMin: number, fromMax: number, toStart: Vector2Like, toEnd: Vector2Like): Vector2Like {
    const t = (value - fromMin) / (fromMax - fromMin)
    return {
        x: toStart.x + t * (toEnd.x - toStart.x),
        y: toStart.y + t * (toEnd.y - toStart.y),
    }
}

//////////////////////////////////////////////////////////////////////////////////////////////////
// Perimeter coordinate mapping

// Maps a point on the unit square boundary to its reference frame U coordinate
export function mapUnitSquareUVToReferenceFrameU(uv: Vector2Like): number {
    if (uv.x < 0) return -mapUnitSquareUVToReferenceFrameU({x: -uv.x, y: uv.y})
    if (uv.y > 0) return 1 - mapUnitSquareUVToReferenceFrameU({x: uv.x, y: -uv.y})

    // bottom right quadrant
    if (uv.x < -uv.y)
        return isInInterval(uv.x, unitSquareUVParams.bottomCenter.x, unitSquareUVParams.bottomRight.x - unitSquareUVParams.cornerRadius)
            ? remapNumber(
                  uv.x,
                  unitSquareUVParams.bottomCenter.x,
                  unitSquareUVParams.bottomRight.x - unitSquareUVParams.cornerRadius,
                  referenceFrameUParams.bottomCenter,
                  referenceFrameUParams.bottomRight - referenceFrameUParams.cornerRadius,
              )
            : remapNumber(
                  uv.x,
                  unitSquareUVParams.bottomRight.x - unitSquareUVParams.cornerRadius,
                  unitSquareUVParams.bottomRight.x,
                  referenceFrameUParams.bottomRight - referenceFrameUParams.cornerRadius,
                  referenceFrameUParams.bottomRight,
              )
    else
        return isInInterval(uv.y, unitSquareUVParams.bottomRight.y, unitSquareUVParams.bottomRight.y + unitSquareUVParams.cornerRadius)
            ? remapNumber(
                  uv.y,
                  unitSquareUVParams.bottomRight.y,
                  unitSquareUVParams.bottomRight.y + unitSquareUVParams.cornerRadius,
                  referenceFrameUParams.bottomRight,
                  referenceFrameUParams.bottomRight + referenceFrameUParams.cornerRadius,
              )
            : remapNumber(
                  uv.y,
                  unitSquareUVParams.bottomRight.y + unitSquareUVParams.cornerRadius,
                  unitSquareUVParams.rightCenter.y,
                  referenceFrameUParams.bottomRight + referenceFrameUParams.cornerRadius,
                  referenceFrameUParams.rightCenter,
              )
}

// Maps a reference frame U coordinate to a point on the unit square boundary (inverse of mapUnitSquareUVToReferenceFrameU)
export function mapReferenceFrameUToUnitSquareUV(u: number): Vector2Like {
    if (u < 0) {
        const uv_ = mapReferenceFrameUToUnitSquareUV(-u)
        return {x: -uv_.x, y: uv_.y}
    }
    if (u > 0.5) {
        const uv_ = mapReferenceFrameUToUnitSquareUV(1 - u)
        return {x: uv_.x, y: -uv_.y}
    }

    // bottom right quadrant
    if (u >= referenceFrameUParams.bottomCenter && u <= referenceFrameUParams.bottomRight - referenceFrameUParams.cornerRadius)
        return remapVector2(
            u,
            referenceFrameUParams.bottomCenter,
            referenceFrameUParams.bottomRight - referenceFrameUParams.cornerRadius,
            unitSquareUVParams.bottomCenter,
            Vector2.fromVector2Like(unitSquareUVParams.bottomRight).sub(Vector2.fromVector2Like({x: unitSquareUVParams.cornerRadius, y: 0})),
        )
    else if (u > referenceFrameUParams.bottomRight - referenceFrameUParams.cornerRadius && u <= referenceFrameUParams.bottomRight)
        return remapVector2(
            u,
            referenceFrameUParams.bottomRight - referenceFrameUParams.cornerRadius,
            referenceFrameUParams.bottomRight,
            Vector2.fromVector2Like(unitSquareUVParams.bottomRight).sub(Vector2.fromVector2Like({x: unitSquareUVParams.cornerRadius, y: 0})),
            unitSquareUVParams.bottomRight,
        )
    else if (u > referenceFrameUParams.bottomRight && u <= referenceFrameUParams.bottomRight + referenceFrameUParams.cornerRadius)
        return remapVector2(
            u,
            referenceFrameUParams.bottomRight,
            referenceFrameUParams.bottomRight + referenceFrameUParams.cornerRadius,
            unitSquareUVParams.bottomRight,
            Vector2.fromVector2Like(unitSquareUVParams.bottomRight).add(Vector2.fromVector2Like({x: 0, y: unitSquareUVParams.cornerRadius})),
        )
    else if (u > referenceFrameUParams.bottomRight + referenceFrameUParams.cornerRadius && u <= referenceFrameUParams.rightCenter)
        return remapVector2(
            u,
            referenceFrameUParams.bottomRight + referenceFrameUParams.cornerRadius,
            referenceFrameUParams.rightCenter,
            Vector2.fromVector2Like(unitSquareUVParams.bottomRight).add(Vector2.fromVector2Like({x: 0, y: unitSquareUVParams.cornerRadius})),
            unitSquareUVParams.rightCenter,
        )

    throw new Error(`Unsupported u value ${u} in reference frame uv to unit square uv conversion`)
}

//////////////////////////////////////////////////////////////////////////////////////////////////
// Reference frame conversions

export function fromParentCanonicalUVToReferenceFrameUV(
    uv: Vector2Like,
    referenceFrameType: MattressReferenceFrameType,
    referenceFrameOrigin: number,
    referenceFrameUnit: number,
    baseMeshGeometryParameters: MattressMeshGeometryParametersData,
): Vector2Like {
    if (!(referenceFrameType === "zAxisRing")) throw new Error(`Unsupported reference frame type ${referenceFrameType}`) // TODO

    const unitSquareUV = mapToUnitSquareUV(uv)

    const uvPointRadius = pointRadius(uv)

    let globalY: number | null = null
    if (uvPointRadius > canonicalUVParams.bottomRoundedEdgeOuterHalfSide) {
        // bottom panel, clip to bottom edge region
        globalY = 0
    } else if (uvPointRadius > canonicalUVParams.sidesRegionOuterHalfSide) {
        // bottom rounded edge

        const tmpUVOuterHalfSide = 1
        const tmpUVInnerHalfSide = tmpUVOuterHalfSide + (baseMeshGeometryParameters.bottomRadius * Math.PI) / 2
        const tmpUV = mapPointBetweenSquareBands(
            uv,
            canonicalUVParams.bottomRoundedEdgeOuterHalfSide,
            canonicalUVParams.bottomRoundedEdgeInnerHalfSide,
            tmpUVOuterHalfSide,
            tmpUVInnerHalfSide,
        )

        globalY = (1 - Math.cos((pointRadius(tmpUV) - tmpUVOuterHalfSide) / baseMeshGeometryParameters.bottomRadius)) * baseMeshGeometryParameters.bottomRadius
    } else if (uvPointRadius > canonicalUVParams.sidesRegionInnerHalfSide) {
        // sides region
        const tmpUVInnerHalfSide = baseMeshGeometryParameters.height - baseMeshGeometryParameters.topRadius
        const tmpUVOuterHalfSide = baseMeshGeometryParameters.bottomRadius

        const tmpUV = mapPointBetweenSquareBands(
            uv,
            canonicalUVParams.sidesRegionOuterHalfSide,
            canonicalUVParams.sidesRegionInnerHalfSide,
            tmpUVOuterHalfSide,
            tmpUVInnerHalfSide,
        )
        globalY = pointRadius(tmpUV)
    } else if (uvPointRadius > canonicalUVParams.topRoundedEdgeInnerHalfSide) {
        // top rounded edge
        const tmpUVInnerHalfSide = baseMeshGeometryParameters.height
        const tmpUVOuterHalfSide = baseMeshGeometryParameters.height - (baseMeshGeometryParameters.topRadius * Math.PI) / 2

        const tmpUV = mapPointBetweenSquareBands(
            uv,
            canonicalUVParams.topRoundedEdgeOuterHalfSide,
            canonicalUVParams.topRoundedEdgeInnerHalfSide,
            tmpUVOuterHalfSide,
            tmpUVInnerHalfSide,
        )

        globalY =
            baseMeshGeometryParameters.height -
            (1 - Math.cos((tmpUVInnerHalfSide - pointRadius(tmpUV)) / baseMeshGeometryParameters.topRadius)) * baseMeshGeometryParameters.topRadius
    } else {
        // top panel, clip to top edge region
        globalY = baseMeshGeometryParameters.height
    }

    if (globalY === null)
        throw new Error(`UV ${uv.x}, ${uv.y} is in the transition region between mattress parts where reference frame uv conversion is not well defined`)
    const referenceFrameU = mapUnitSquareUVToReferenceFrameU(unitSquareUV)
    const referenceFrameV = (globalY - referenceFrameOrigin) / referenceFrameUnit

    return {x: referenceFrameU, y: referenceFrameV}
}

export function toParentCanonicalUVFromReferenceFrameUV(
    uv: Vector2Like,
    referenceFrameType: MattressReferenceFrameType,
    referenceFrameOrigin: number,
    referenceFrameUnit: number,
    baseMeshGeometryParameters: MattressMeshGeometryParametersData,
): Vector2Like {
    if (!(referenceFrameType === "zAxisRing")) throw new Error(`Unsupported reference frame type ${referenceFrameType}`) // TODO

    const unitSquareUV = mapReferenceFrameUToUnitSquareUV(uv.x)
    const globalY = Math.min(referenceFrameOrigin + uv.y * referenceFrameUnit, baseMeshGeometryParameters.height) // clamp to mattress height

    // bottom rounded edge
    if (globalY <= baseMeshGeometryParameters.bottomRadius) {
        const tmpUVOuterHalfSide = 1
        const tmpUVInnerHalfSide = tmpUVOuterHalfSide + (baseMeshGeometryParameters.bottomRadius * Math.PI) / 2

        const tmpUVScale = tmpUVOuterHalfSide + Math.acos(1 - globalY / baseMeshGeometryParameters.bottomRadius) * baseMeshGeometryParameters.bottomRadius
        const tmpUV = {x: unitSquareUV.x * tmpUVScale, y: unitSquareUV.y * tmpUVScale}

        return mapPointBetweenSquareBands(
            tmpUV,
            tmpUVOuterHalfSide,
            tmpUVInnerHalfSide,
            canonicalUVParams.bottomRoundedEdgeOuterHalfSide,
            canonicalUVParams.bottomRoundedEdgeInnerHalfSide,
        )
    } else if (globalY <= baseMeshGeometryParameters.height - baseMeshGeometryParameters.topRadius) {
        // sides region
        const tmpUVInnerHalfSide = baseMeshGeometryParameters.height - baseMeshGeometryParameters.topRadius
        const tmpUVOuterHalfSide = baseMeshGeometryParameters.bottomRadius

        return mapPointBetweenSquareBands(
            {x: unitSquareUV.x * globalY, y: unitSquareUV.y * globalY},
            tmpUVOuterHalfSide,
            tmpUVInnerHalfSide,
            canonicalUVParams.sidesRegionOuterHalfSide,
            canonicalUVParams.sidesRegionInnerHalfSide,
        )
    } else {
        // top rounded edge
        const tmpUVInnerHalfSide = baseMeshGeometryParameters.height
        const tmpUVOuterHalfSide = baseMeshGeometryParameters.height - (baseMeshGeometryParameters.topRadius * Math.PI) / 2

        const tmpUVScale =
            tmpUVInnerHalfSide -
            Math.acos(1 - (baseMeshGeometryParameters.height - globalY) / baseMeshGeometryParameters.topRadius) * baseMeshGeometryParameters.topRadius
        const tmpUV = {x: unitSquareUV.x * tmpUVScale, y: unitSquareUV.y * tmpUVScale}

        return mapPointBetweenSquareBands(
            tmpUV,
            tmpUVOuterHalfSide,
            tmpUVInnerHalfSide,
            canonicalUVParams.topRoundedEdgeOuterHalfSide,
            canonicalUVParams.topRoundedEdgeInnerHalfSide,
        )
    }
}

export function toParentReferenceFrameUVFromReferenceFrameUV(
    uv: Vector2Like,
    type: MattressReferenceFrameType,
    parentType: MattressReferenceFrameType,
    origin: number,
    unit: number,
): Vector2Like {
    if (type !== "zAxisRing") throw new Error(`Unsupported to parent reference frame uv conversion from ${type}`) // TODO
    if (type !== parentType) throw new Error(`Unsupported to parent reference frame uv conversion from ${type} to ${parentType}`) // TODO

    return {x: uv.x, y: origin + uv.y * unit}
}

export function fromParentReferenceFrameUVToReferenceFrameUV(
    uv: Vector2Like,
    type: MattressReferenceFrameType,
    parentType: MattressReferenceFrameType,
    origin: number,
    unit: number,
): Vector2Like {
    if (type !== "zAxisRing") throw new Error(`Unsupported to parent reference frame uv conversion from ${type}`) // TODO
    if (type !== parentType) throw new Error(`Unsupported to parent reference frame uv conversion from ${type} to ${parentType}`) // TODO

    return {x: uv.x, y: (uv.y - origin) / unit}
}
